/**
 * LocationSelector — the persistent, prominent header control (Tractor-Supply
 * style): pin icon + selected clinic city, or a clear CTA when unset.
 *
 * Visible in BOTH the desktop header and the mobile header bar (outside the
 * hamburger — that is the entire point of the request). Opens the SAME picker
 * UI as the first-visit gate (shared LocationPicker, no duplication) so the
 * user can change clinic at any time, geolocation included.
 *
 * If the user is currently on a /:locationSlug/:serviceSlug page, choosing a new
 * clinic navigates to the SAME service under the new location so they keep their
 * place instead of being dumped on a hub page.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaMapMarkerAlt, FaChevronDown } from "react-icons/fa";
import { ApplicationContext } from "../../context/GlobalState";
import { useLocale } from "../../context/LocaleContext";
import { t } from "../../i18n";
import {
  brandLocations,
  locationFromPath,
  serviceFromPath,
} from "../../utils/locationResolver";
import LocationPicker, { LocationDialog } from "../LocationPicker/LocationPicker";
import "./LocationSelector.css";

/** Window events that open a specific selector instance's picker. */
export const LOCATION_PICKER_EVENTS = {
  desktop: "apexosn:open-location-picker:desktop",
  mobile: "apexosn:open-location-picker:mobile",
};

export default function LocationSelector({ variant, openEvent }) {
  const { state, dispatch } = useContext(ApplicationContext);
  const { locale } = useLocale();
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Remote opener: the Services mega-menu shows a "choose your location" CTA
  // when the clinic is unknown, and opens THIS picker via a window event so the
  // dialog implementation is never duplicated. Scoped per instance name so the
  // desktop and mobile selectors can never both open at once.
  useEffect(() => {
    if (!openEvent) return undefined;
    const handler = () => setOpen(true);
    window.addEventListener(openEvent, handler);
    return () => window.removeEventListener(openEvent, handler);
  }, [openEvent]);

  const brand = state.brand;
  const locations = useMemo(
    () => brandLocations(brand, state.taxonomy),
    [brand, state.taxonomy]
  );

  const urlSlug = locationFromPath(routerLocation.pathname, locations);
  const activeSlug = urlSlug || state.selectedLocationSlug || null;
  const active = locations.find((l) => l.slug === activeSlug) || null;

  const onSelect = useCallback(
    (slug) => {
      dispatch({ type: "SET_SELECTED_LOCATION", payload: slug });
      setOpen(false);
      // Keep the user's place when they are on a city × service page.
      const service = serviceFromPath(routerLocation.pathname, locations);
      if (service) {
        const prefix = locale === "en" ? "" : `/${locale}`;
        navigate(`${prefix}/${slug}/${service}/`);
      }
    },
    [dispatch, locale, locations, navigate, routerLocation.pathname]
  );

  if (locations.length < 2) return null;

  const label = active
    ? `${active.city}, ${active.state}`
    : t("locationGate.chooseLocation", locale);

  return (
    <>
      <button
        type="button"
        className={`locsel${active ? "" : " locsel-unset"}${variant ? ` locsel-${variant}` : ""}`}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-pseo-location
        data-pseo-cta="header_location_selector"
        data-pseo-cta-text={label}
      >
        <FaMapMarkerAlt className="locsel-pin" aria-hidden="true" />
        <span className="locsel-text">
          <span className="locsel-eyebrow">
            {active
              ? t("locationGate.yourClinic", locale)
              : t("locationGate.setClinic", locale)}
          </span>
          <span className="locsel-value">{label}</span>
        </span>
        <FaChevronDown className="locsel-caret" aria-hidden="true" />
      </button>

      {open && (
        <LocationDialog
          locale={locale}
          titleKey="locationGate.changeTitle"
          onClose={() => setOpen(false)}
          variant="selector"
        >
          <LocationPicker
            locale={locale}
            brand={brand}
            locations={locations}
            selectedSlug={activeSlug}
            onSelect={onSelect}
            descriptionKey="locationGate.changeDescription"
          />
        </LocationDialog>
      )}
    </>
  );
}
